import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AppShell } from '@mantine/core'
import { AppHeader } from './components/AppHeader'
import { AppNavbar } from './components/AppNavbar'
import { AppFooter } from './components/AppFooter'
import { DashboardPage } from './pages/DashboardPage'
import { JobsPage } from './pages/JobsPage'
import { EtlActivityPage } from './pages/EtlActivityPage'
import { LoginPage } from './pages/LoginPage'
import { AuditLogPage } from './pages/audit/AuditLogPage'
import { DesignSystemPage } from './pages/DesignSystemPage'
import { EtlExplorerPage } from './pages/EtlExplorerPage'
import { DataCombPage } from './pages/DataCombPage'
import { WorkersPage } from './pages/WorkersPage'
import { SourcesPage } from './pages/SourcesPage'
import { ConnectionsPage } from './pages/ConnectionsPage'
import { ConfigurationPage } from './pages/ConfigurationPage'
import { ScheduleLoadPage } from './pages/ScheduleLoadPage'
import { ExecutionHistoryPage } from './pages/ExecutionHistoryPage'
import { GovernanceQueuePage } from './pages/GovernanceQueuePage'
import { ReportRegistryPage } from './pages/ReportRegistryPage'
import { RetirementPage } from './pages/RetirementPage'
import { RequestBackstopPage } from './pages/RequestBackstopPage'
import { ReportDetailPage } from './pages/ReportDetailPage'
import { QueuesPage } from './pages/QueuesPage'
import { HealthMonitoringPage } from './pages/HealthMonitoringPage'
import { SignalInboxPage } from './pages/SignalInboxPage'
import { NotFoundPage } from './pages/NotFoundPage'
import { useState, useEffect } from 'react'
import { useTheme } from './theme'
import { useAuth, isEntraConfigured } from './auth'
import { useSignalR } from './hooks/useSignalR'

function useSwaAuth() {
  const [status, setStatus] = useState<'loading' | 'authenticated' | 'unauthenticated'>('loading')

  useEffect(() => {
    fetch('/.auth/me')
      .then(res => {
        if (!res.ok) throw new Error('Auth endpoint not available')
        return res.json()
      })
      .then(data => {
        setStatus(data.clientPrincipal ? 'authenticated' : 'unauthenticated')
      })
      .catch(() => {
        setStatus('unauthenticated')
      })
  }, [])

  return { status }
}

function AppLayout() {
  const [navbarOpened, setNavbarOpened] = useState(false)
  const { mode } = useTheme()
  useSignalR()

  const textureClasses = mode === 'light'
    ? 'grain-texture-light paper-fiber-light weathered-edges-light vignette-light'
    : 'grain-texture-dark weathered-edges-dark vignette-dark'

  return (
    <div className={textureClasses}>
      <AppShell
        // layout="alt" gives the rail the full viewport height and starts the
        // header at its right edge, so nothing sits above the rail. This matches
        // the mockup, where the OBSERVATORY brand block is the top-left corner of
        // the app rather than something tucked under a site-wide bar.
        layout="alt"
        header={{ height: 60 }}
        navbar={{
          width: 250,
          breakpoint: 'sm',
          collapsed: { mobile: !navbarOpened },
        }}
        padding="md"
        styles={{
          main: {
            backgroundColor: 'var(--color-bg-main)',
          },
        }}
      >
        <AppShell.Header style={{ backgroundColor: 'var(--color-bg-surface)' }}>
          <AppHeader navbarOpened={navbarOpened} setNavbarOpened={setNavbarOpened} />
        </AppShell.Header>

        {/* The rail is constant navy in both modes — not a surface. */}
        <AppShell.Navbar style={{ backgroundColor: 'var(--rail-bg)', borderColor: 'var(--rail-border)' }}>
          <AppNavbar />
        </AppShell.Navbar>

        <AppShell.Main>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/jobs" element={<JobsPage />} />
            <Route path="/etl-activity" element={<EtlActivityPage />} />
            {/* /etl-explorer lists recorded entity runs; /etl-explorer/:runId is one entity run. */}
            <Route path="/etl-explorer" element={<EtlExplorerPage />} />
            <Route path="/etl-explorer/:runId" element={<EtlExplorerPage />} />
            {/* Every ETL run as one row, over Etl.EtlRun: served by
                ObservatoryExecutionsController at api/observatory/executions, bare
                [Authorize]. /execution-history/:runId is one run with its entity rows --
                the Explorer's run key and the Scheduler's request history link there.
                Takes ?status=&module=&entity=&from=&to=&page= so a filtered list is a link. */}
            <Route path="/execution-history" element={<ExecutionHistoryPage />} />
            <Route path="/execution-history/:runId" element={<ExecutionHistoryPage />} />
            {/* The Data Comb's findings. Served by real endpoints over the three
                Etl.DataComb* tables the nightly sweep writes -- no mock layer. */}
            <Route path="/data-comb" element={<DataCombPage />} />
            {/* The background-job fleet. Served by ObservatoryWorkersController over
                Etl.WorkerHeartbeat, which Automaton projects out of Hangfire's own
                server list -- the same poll the Overview panel already renders, with
                the eight fields that panel discards. No backend work; front-end only. */}
            <Route path="/workers" element={<WorkersPage />} />
            {/* The load forecast: scheduled reports and scheduled jobs against the one
                shared worker pool. Served by ObservatoryScheduleLoadController under
                LighthouseAdmin, READ-ONLY -- the hand-off to Cartographer's Town Crier
                is a link, not a write. `/schedules-load`, not `/schedules`: that path
                is Town Crier's, in Cartographer, and the two must not be confused.
                Takes ?hours= so a window is a link somebody can send. */}
            <Route path="/schedules-load" element={<ScheduleLoadPage />} />
            {/* Upstream source reachability. Served by ObservatorySourcesController over
                Etl.SourceConnectionHealth, which Automaton's SourceHealthWorker publishes
                every 60s -- one row per connector per host. The API holds no Oracle package
                (D14), so it reports what the worker's connectors observed rather than
                dialling anything. No backend work; front-end only. */}
            <Route path="/sources" element={<SourcesPage />} />
            {/* The append-only counterpart to /sources: every connection the ETL opened
                over 30 days, served by ObservatoryConnectionsController from
                Etl.EtlRunChunkAttempt. Takes ?runId= to pivot from an entity run.
                Paged and filtered server-side -- up to ~6,300 rows. Front-end only. */}
            <Route path="/connections" element={<ConnectionsPage />} />
            {/* Every allowlisted setting, per host, READ-ONLY. Served by
                ObservatoryConfigurationController: the API's own startup snapshot
                plus Automaton's projection in Etl.HostConfiguration (D14), under
                LighthouseAdmin. No write path exists and none is routed here. */}
            <Route path="/configuration" element={<ConfigurationPage />} />
            {/* Dead-letter queue depths, served by DeadLetterController at
                api/system/dead-letters. The depths are bare [Authorize]; the message
                peek is LighthouseAdmin, and the page gates it itself. */}
            <Route path="/queues" element={<QueuesPage />} />
            {/* Health Monitoring: one board, per host, over what is ALREADY measured --
                the API's own /api/health checks, the worker fleet
                (api/observatory/workers) and source reachability
                (api/observatory/sources). No table or endpoint of its own; it names
                what nothing checks rather than colouring it. `/system-health`, never
                `/health…`: Vite proxies /health to the API by PREFIX
                (vite.config.ts), so a /health-monitoring route would be answered by
                the API in development. navModel.test.ts refuses any such path. */}
            <Route path="/system-health" element={<HealthMonitoringPage />} />
            {/* The Signal Inbox: open and resolved operational signals from
                Etl.OperationalSignal*, served by ObservatorySignalsController at
                api/observatory/signals, bare [Authorize]. Automaton's detector alone
                raises and resolves -- from five rules over conditions Observatory
                already measures. People acknowledge, assign and release a signal
                (D6b(i)) through ObservatorySignalHandlingController, LighthouseAdmin AND
                JwtBearer-pinned, which writes Etl.OperationalSignalHandling and never
                the signal row; there is no resolve and no snooze, so an acknowledged
                signal stays open and counted. Before migration AddOperationalSignals
                the list answers 503 and the page says so. */}
            <Route path="/signals" element={<SignalInboxPage />} />

            {/* The Cartographer section: Observatory's own LighthouseAdmin read
                controllers over Cartographer's tables (D1a), READ-ONLY -- every change
                is a hand-off to Cartographer, except the request backstop below. `/governance-queue`, never `/governance`:
                that is Cartographer's route (Cartographer App.tsx:123), and this page
                links to it. Takes ?finding= to open one group. */}
            <Route path="/governance-queue" element={<GovernanceQueuePage />} />
            <Route path="/registry" element={<ReportRegistryPage />} />
            {/* Report Detail (D5): one report, seven READ-ONLY tabs -- Overview,
                Governance, Operations, Usage, Scheduling, Access, History -- and no
                Lineage, which needs semantic models that do not exist. Served by
                ObservatoryReportDetailController under api/observatory/registry/{id},
                LighthouseAdmin at class level; Access reuses the access explanation and
                History the audit trail's entityId filter. Takes ?tab= and ?days=7|30|90
                (utils/reportDetailPath). Under /registry so the rail highlights Report
                Registry; Cartographer has no /registry route, and none is proxied. No
                verb on this page acts on a report (D6a): every change is Cartographer's. */}
            <Route path="/registry/:reportId" element={<ReportDetailPage />} />
            {/* Retirement candidates and what happened to each one flagged. Takes ?reason=. */}
            <Route path="/retirement" element={<RetirementPage />} />
            {/* The request backstop (Cartographer doc 15 §7): the Cartographer requests that fell through, served by
                ObservatoryRequestsBackstopController at api/observatory/requests, LighthouseAdmin at class level. The
                ONE act surface in this section (D6a as amended by Q-2b = a): its interventions are
                ICartographerRequestBackstop's, LighthouseAdmin AND JwtBearer-pinned, gated on canHandleRequests, and
                none approves anything. /requests/:requestId opens one request in the drawer — the path a Cartographer
                notice's observatory:/requests/{id} link names. */}
            <Route path="/requests" element={<RequestBackstopPage />} />
            <Route path="/requests/:requestId" element={<RequestBackstopPage />} />

            {/* Re-homed with the Data Entry removal: the audit trail is not a data-entry
                surface, and its only caller was this app. */}
            <Route path="/audit" element={<AuditLogPage />} />
            {/* Living reference for the shared design system. Deliberately not in
                the nav — it is a developer surface, not a product one. */}
            <Route path="/design-system" element={<DesignSystemPage />} />
            {/* LAST: anything unmatched — a stale /data-entry bookmark above all — says
                so, instead of rendering the shell around an empty body. */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <AppFooter />
        </AppShell.Main>
      </AppShell>
    </div>
  )
}

// Used when Entra ID env vars are configured (local dev with MSAL)
function MsalGuardedApp() {
  const { isAuthenticated, isLoading } = useAuth()
  const location = useLocation()

  if (isLoading) return null

  if (location.pathname === '/login') {
    if (isAuthenticated) return <Navigate to="/dashboard" replace />
    return <LoginPage />
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />
  return <AppLayout />
}

// Used in production on Azure Static Web Apps (built-in auth)
function SwaGuardedApp() {
  const location = useLocation()
  const { status } = useSwaAuth()

  if (location.pathname === '/login') {
    if (status === 'authenticated') return <Navigate to="/dashboard" replace />
    return <LoginPage />
  }

  if (status === 'loading') return null
  if (status === 'unauthenticated') return <Navigate to="/login" replace />
  return <AppLayout />
}

function App() {
  // Entra configured (MSAL available) → use MSAL popup auth
  // Otherwise → use SWA built-in /.auth/me check
  if (isEntraConfigured()) {
    return <MsalGuardedApp />
  }
  return <SwaGuardedApp />
}

export default App
